import { supabase } from '@/integrations/supabase/client';
import { processDbOperationsWithControl } from '@/utils/concurrencyControl';
import { buildCourseAssessmentProgress } from '@/utils/assessmentProgress';

// Simple logger for dashboard service
const logger = {
  debug: (message: string, ...args: any[]) => console.log(message, ...args),
  error: (message: string, ...args: any[]) => console.error(message, ...args),
  warn: (message: string, ...args: any[]) => console.warn(message, ...args),
  info: (message: string, ...args: any[]) => console.info(message, ...args)
};

// Types for grading response structure
interface GradingResponse {
  score: number;
  summary?: string;
  gradedAt?: string;
}

// Types for activity tracking
export interface ProgressActivity {
  id: string;
  type: 'step_completed' | 'quiz_completed' | 'course_started' | 'roadmap_completed';
  title: string;
  description?: string;
  timestamp: string;
  courseId?: string;
  roadmapId?: string;
  points?: number;
}

// Types for score trends over time
export interface ScoreTrend {
  date: string;
  score: number;
  courseId: string;
  courseName: string;
  type: 'quiz' | 'execution' | 'combined';
}

// Summary statistics for the dashboard
export interface DashboardStats {
  totalCourses: number;
  completedCourses: number;
  totalPoints: number;
  averageScore: number;
  streakDays: number;
  lastActivityDate: string | null;
}

// Types for dashboard data structures
export interface DashboardCourse {
  id: string;
  title: string;
  description: string | null;
  enrolledAt: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'archived';
  roadmaps: DashboardRoadmap[];
  overallProgress: {
    completed: number;
    total: number;
    percentage: number;
  };
  highestScore: number | null;
  averageScore: number | null;
  // Separate score tracking
  bestQuizScore: number | null;
  bestExecutionScore: number | null;
  bestCombinedScore: number | null;
  lastActivity: string | null;
  estimatedTimeRemaining: string | null;
  totalPossiblePoints: number;
  currentPoints: number;
}

export interface DashboardRoadmap {
  id: string;
  title: string;
  description: string | null;
  instanceId: string | null;
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };
  isCompleted: boolean;
  isStarted: boolean;
  lastActivity: string | null;
  estimatedTimeRemaining: string | null;
  highestScore: number | null;
  averageScore: number | null;
}


class DashboardService {
  /**
   * Get lightweight dashboard data for overview (no detailed roadmap processing)
   */
  async getDashboardData(userId: string): Promise<{
    courses: DashboardCourse[];
    stats: DashboardStats;
    recentActivity: ProgressActivity[];
    scoreTrends: ScoreTrend[];
  }> {

    // Fetch enrollments ONCE here; both lightweight helpers used to issue the
    // identical course_enrollments query independently.
    const { data: enrollments } = await supabase
      .from('course_enrollments')
      .select('course_id, enrolled_at')
      .eq('user_id', userId);
    const enrollmentRows = enrollments ?? [];

    const [courses, stats, recentActivity, scoreTrends] = await Promise.all([
      this.getLightweightUserCourses(userId, undefined, enrollmentRows),
      this.getLightweightDashboardStats(userId, enrollmentRows.length),
      this.getRecentActivity(userId),
      this.getScoreTrends(userId)
    ]);

    return {
      courses,
      stats,
      recentActivity,
      scoreTrends
    };
  }

  /**
   * Get comprehensive dashboard data for a user (full version)
   */
  async getFullDashboardData(userId: string): Promise<{
    courses: DashboardCourse[];
    stats: DashboardStats;
    recentActivity: ProgressActivity[];
    scoreTrends: ScoreTrend[];
  }> {

    const [courses, stats, recentActivity, scoreTrends] = await Promise.all([
      this.getUserCourses(userId),
      this.getDashboardStats(userId),
      this.getRecentActivity(userId),
      this.getScoreTrends(userId)
    ]);

    return {
      courses,
      stats,
      recentActivity,
      scoreTrends
    };
  }

  /**
   * Get lightweight course data for dashboard overview (no roadmap processing)
   */
  async getLightweightUserCourses(
    userId: string,
    companyId?: string,
    preloadedEnrollments?: Array<{ course_id: string; enrolled_at: string }>
  ): Promise<DashboardCourse[]> {
    try {
      logger.debug(`🚀 DASHBOARD_FAST: Loading lightweight courses for user ${userId}`);

      // Get enrolled courses (skipped when the caller already fetched them)
      let enrollments = preloadedEnrollments ?? null;
      if (!enrollments) {
        let enrollmentQuery = supabase
          .from('course_enrollments')
          .select('course_id, enrolled_at')
          .eq('user_id', userId);

        if (companyId) {
          const { data: links } = await supabase
            .from('course_companies').select('course_id').eq('company_id', companyId);
          const allowedCourseIds = (links || []).map((l: any) => l.course_id);
          enrollmentQuery = enrollmentQuery.in('course_id', allowedCourseIds.length ? allowedCourseIds : ['00000000-0000-0000-0000-000000000000']);
        }

        const { data, error: enrollmentError } = await enrollmentQuery;

        if (enrollmentError) {
          logger.error('📝 DASHBOARD_FAST: Error fetching enrollments:', enrollmentError);
          return [];
        }
        enrollments = data ?? [];
      }

      if (!enrollments?.length) {
        logger.debug('📝 DASHBOARD_FAST: No course enrollments found');
        return [];
      }

      // Get basic course details only (no roadmap processing) - exclude archived
      const courseIds = enrollments.map(e => e.course_id);
      const { data: courses, error: courseError } = await supabase
        .from('courses')
        .select('id, title, description, is_active, total_possible_points, is_archived')
        .in('id', courseIds)
        .or('is_archived.is.null,is_archived.eq.false');

      if (courseError || !courses?.length) {
        return [];
      }

      console.log(`🚀 DASHBOARD_FAST: Loaded ${courses.length} courses (no roadmap processing)`);

      // Return minimal course data for overview
      return courses.map(course => {
        const enrollment = enrollments.find(e => e.course_id === course.id);
        return {
          id: course.id,
          title: course.title,
          description: course.description,
          enrolledAt: enrollment?.enrolled_at || new Date().toISOString(),
          status: 'in_progress' as const, // Default status for overview
          roadmaps: [], // Empty for lightweight loading
          overallProgress: { completed: 0, total: 1, percentage: 0 }, // Placeholder
          highestScore: null,
          averageScore: null,
          bestQuizScore: null,
          bestExecutionScore: null,
          bestCombinedScore: null,
          lastActivity: null,
          estimatedTimeRemaining: null,
          totalPossiblePoints: course.total_possible_points || 240,
          currentPoints: 0
        } as DashboardCourse;
      });

    } catch (error) {
      console.error('❌ DASHBOARD_FAST: Error loading lightweight courses:', error);
      return [];
    }
  }

  /**
   * Get lightweight dashboard stats (no heavy processing)
   */
  async getLightweightDashboardStats(userId: string, enrollmentCount?: number): Promise<DashboardStats> {
    try {
      // Just count enrollments for basic stats (skip the query when the caller
      // already knows the count)
      let totalCourses = enrollmentCount ?? -1;
      if (totalCourses < 0) {
        const { data: enrollments } = await supabase
          .from('course_enrollments')
          .select('course_id')
          .eq('user_id', userId);
        totalCourses = enrollments?.length || 0;
      }

      return {
        totalCourses,
        completedCourses: 0, // Will calculate when needed
        totalPoints: 0,
        averageScore: 0,
        streakDays: 0,
        lastActivityDate: null
      };

    } catch (error) {
      console.error('❌ DASHBOARD_FAST: Error calculating lightweight stats:', error);
      return {
        totalCourses: 0,
        completedCourses: 0,
        totalPoints: 0,
        averageScore: 0,
        streakDays: 0,
        lastActivityDate: null
      };
    }
  }

  async getUserCourses(userId: string, companyId?: string): Promise<DashboardCourse[]> {

    try {
      console.log(`🎯 DASHBOARD: Loading courses for user ${userId}${companyId ? ` in company ${companyId}` : ''}`);

      // Get enrolled courses - enhanced for widget mode
      let enrollmentQuery = supabase
        .from('course_enrollments')
        .select('course_id')
        .eq('user_id', userId);

      // Scope to the company's courses via course_companies (no phantom company_id column)
      if (companyId) {
        const { data: links } = await supabase
          .from('course_companies').select('course_id').eq('company_id', companyId);
        const allowedCourseIds = (links || []).map((l: any) => l.course_id);
        enrollmentQuery = enrollmentQuery.in('course_id', allowedCourseIds.length ? allowedCourseIds : ['00000000-0000-0000-0000-000000000000']);
      }

      const { data: enrollments, error: enrollmentError } = await enrollmentQuery;

      if (enrollmentError) {
        console.error('❌ DASHBOARD: Error fetching enrollments:', enrollmentError);
        return [];
      }

      if (!enrollments?.length) {
        console.log('📝 DASHBOARD: No course enrollments found');
        return [];
      }

      console.log(`📚 DASHBOARD: Found ${enrollments.length} course enrollments`);

      // Extract course IDs for batch processing
      const courseIds = enrollments.map(e => e.course_id);

      // Now process each course ID
      const courses = await processDbOperationsWithControl(
        courseIds,
        async (courseId) => {
          // Get course details (exclude archived)
          const { data: course, error: courseError } = await supabase
            .from('courses')
            .select('id, title, description, is_active, total_possible_points, is_archived')
            .eq('id', courseId)
            .or('is_archived.is.null,is_archived.eq.false')
            .single();

          if (courseError || !course) return null;

          // Get roadmaps for this course
          const roadmaps = await this.getCourseRoadmaps(userId, courseId, companyId);

          // Calculate overall course progress
          const overallProgress = this.calculateOverallProgress(roadmaps);

          // Get highest and average scores
          const { highestScore, averageScore } = this.calculateCourseScores(roadmaps);

          // Grading disabled - return defaults
          const bestQuizScore = null;
          const bestExecutionScore = null;
          const combinedBestScore = null;

          // Determine course status
          const status = this.determineCourseStatus(roadmaps, overallProgress);

          // Get last activity
          const lastActivity = this.getLastActivity(roadmaps);

          // Grading disabled - return defaults
          const currentPoints = 0;
          const totalPossiblePoints = course.total_possible_points || 0;

          return {
            id: course.id,
            title: course.title,
            description: course.description,
            enrolledAt: new Date().toISOString(), // For widget mode, use current time
            status,
            roadmaps,
            overallProgress,
            highestScore,
            averageScore,
            // Add separate score tracking
            bestQuizScore,
            bestExecutionScore,
            bestCombinedScore: combinedBestScore,
            lastActivity,
            estimatedTimeRemaining: this.estimateTimeRemaining(overallProgress),
            totalPossiblePoints: totalPossiblePoints || course.total_possible_points || 0,
            currentPoints
          } as DashboardCourse;
        },
        {
          concurrencyLimit: 3,
          delayBetweenBatches: 200,
          operationName: 'course processing'
        }
      );

      const validCourses = courses.filter(Boolean) as DashboardCourse[];
      console.log(`✅ DASHBOARD: Loaded ${validCourses.length} courses`);
      return validCourses;

    } catch (error) {
      console.error('❌ DASHBOARD: Error loading user courses:', error);
      return [];
    }
  }

  /**
   * Get roadmaps for a specific course - RESTORED TO WORKING VERSION
   */
  async getCourseRoadmaps(userId: string, courseId: string, companyId?: string): Promise<DashboardRoadmap[]> {

    try {
      // Get roadmaps for this course (exclude archived roadmaps)
      const { data: roadmapCourses, error: roadmapError } = await supabase
        .from('roadmap_courses')
        .select(`
          roadmap_id,
          roadmap_templates!inner (
            id, title, description, is_archived
          )
        `)
        .eq('course_id', courseId);

      if (roadmapError || !roadmapCourses?.length) {
        return [];
      }

      // Filter out archived roadmaps
      const nonArchivedRoadmaps = roadmapCourses.filter((rc: any) => {
        const template = rc.roadmap_templates;
        return !template?.is_archived;
      });

      const { data: stepAssessmentLinks } = await supabase
        .from('step_assessments')
        .select('id, course_id, step_id, template_id, required_for_completion, settings')
        .eq('course_id', courseId);
      const linkedStepIds = [...new Set((stepAssessmentLinks || []).map((link: any) => link.step_id).filter(Boolean))];
      const { data: linkedSteps } = linkedStepIds.length > 0
        ? await supabase
          .from('steps')
          .select('id, roadmap_id')
          .in('id', linkedStepIds)
        : { data: [] };
      const linkedStepMap = new Map((linkedSteps || []).map((step: any) => [step.id, step]));
      const assessmentLinksWithSteps = (stepAssessmentLinks || []).map((link: any) => ({
        ...link,
        step: linkedStepMap.get(link.step_id) || null,
      }));
      const assessmentTemplateIds = [...new Set(assessmentLinksWithSteps.map((link: any) => link.template_id).filter(Boolean))];
      const { data: assessmentSubmissions } = assessmentTemplateIds.length > 0
        ? await supabase
          .from('assessment_submissions')
          .select('id, template_id, course_id, step_id, status, metadata, score_percent, completed_at, graded_at, updated_at, created_at')
          .eq('user_id', userId)
          // Course-scoped: templates are shared across cohort copies, so template_id
          // alone counts sibling-course attempts toward this course's progress.
          .eq('course_id', courseId)
          .in('template_id', assessmentTemplateIds)
        : { data: [] };
      const { data: assessmentTemplates } = assessmentTemplateIds.length > 0
        ? await supabase
          .from('assessment_templates')
          .select('id, pass_threshold, settings')
          .in('id', assessmentTemplateIds)
        : { data: [] };
      const templatesById = Object.fromEntries(
        (assessmentTemplates || []).map((t: any) => [t.id, { pass_threshold: t.pass_threshold, settings: t.settings }]),
      );
      const assessmentProgressByRoadmap = buildCourseAssessmentProgress({
        stepAssessments: assessmentLinksWithSteps,
        submissions: assessmentSubmissions || [],
        hasEnrollment: true,
        templatesById,
      });

      // Process each roadmap to get user progress
      const roadmaps = await processDbOperationsWithControl(
        nonArchivedRoadmaps,
        async (roadmapCourse: any) => {
          const template = roadmapCourse.roadmap_templates;
          if (!template) return null;

          // Get user's roadmap instance only for open-work linkage and last activity.
          let instanceQuery = supabase
            .from('user_roadmap_instances')
            .select('id, created_at, updated_at')
            .eq('user_id', userId)
            .eq('template_id', template.id);

          // (removed) user_roadmap_instances has no company_id column; company scoping
          // is enforced by the course_companies-derived enrollment filter above.

          const { data: instances } = await instanceQuery.limit(1);
          const instance = instances?.[0] || null;

          const roadmapAssessmentProgress = assessmentProgressByRoadmap[template.id];
          const progress = roadmapAssessmentProgress
            ? {
              completed: roadmapAssessmentProgress.completed,
              total: roadmapAssessmentProgress.total,
              percentage: roadmapAssessmentProgress.percentage,
            }
            : { completed: 0, total: 0, percentage: 0 };
          const hasActiveAssessmentWork = Boolean(
            roadmapAssessmentProgress &&
            (
              roadmapAssessmentProgress.completed > 0 ||
              roadmapAssessmentProgress.statusCounts.in_progress > 0 ||
              roadmapAssessmentProgress.statusCounts.submitted > 0 ||
              roadmapAssessmentProgress.statusCounts.in_review > 0 ||
              roadmapAssessmentProgress.statusCounts.needs_revision > 0 ||
              roadmapAssessmentProgress.statusCounts.failed > 0 ||
              roadmapAssessmentProgress.statusCounts.earned > 0
            )
          );

          // Get roadmap scores - RESTORED TO WORKING PATTERN
          const { highestScore, averageScore } = await this.getRoadmapScores(userId, template.id, instance?.id);

          return {
            id: template.id,
            title: template.title,
            description: template.description,
            instanceId: instance?.id || null,
            progress,
            isCompleted: progress.percentage >= 100,
            isStarted: hasActiveAssessmentWork,
            lastActivity: instance?.updated_at || null,
            estimatedTimeRemaining: this.estimateTimeRemaining(progress),
            highestScore: highestScore,
            averageScore: averageScore
          } as DashboardRoadmap;
        },
        {
          concurrencyLimit: 5,
          delayBetweenBatches: 100,
          operationName: 'roadmap processing'
        }
      );

      return roadmaps.filter(Boolean) as DashboardRoadmap[];

    } catch (error) {
      console.error('❌ DASHBOARD: Error loading course roadmaps:', error);
      return [];
    }
  }

  /**
   * Get scores for a roadmap from grading data - RESTORED TO WORKING VERSION
   */
  async getRoadmapScores(userId: string, templateId: string, instanceId?: string): Promise<{
    highestScore: number | null;
    averageScore: number | null;
  }> {
    try {
      let query = supabase
        .from('user_field_responses')
        .select('grading_response, created_at')
        .eq('user_id', userId)
        .not('grading_response', 'is', null);

      // Filter by instance if available, otherwise by template
      if (instanceId) {
        query = query.eq('instance_id', instanceId);
      } else {
        query = query.eq('template_id', templateId);
      }

      const { data: responses, error } = await query;

      if (error || !responses?.length) {
        return { highestScore: null, averageScore: null };
      }

      // Extract scores from grading responses
      const scores: number[] = [];
      responses.forEach(response => {
        if (response.grading_response && typeof response.grading_response === 'object') {
          const gradingData = response.grading_response as any;
          const score = typeof gradingData.overall?.score === 'number' ? gradingData.overall.score : gradingData.score;
          if (typeof score === 'number') {
            scores.push(score);
          }
        }
      });

      if (scores.length === 0) {
        return { highestScore: null, averageScore: null };
      }

      const highestScore = Math.max(...scores);
      const averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;

      return {
        highestScore: Math.round(highestScore * 10) / 10,
        averageScore: Math.round(averageScore * 10) / 10
      };

    } catch (error) {
      console.error('❌ DASHBOARD: Error getting roadmap scores:', error);
      return { highestScore: null, averageScore: null };
    }
  }

  /**
   * Calculate overall progress across all roadmaps
   */
  calculateOverallProgress(roadmaps: DashboardRoadmap[]): { completed: number; total: number; percentage: number } {
    const totalCompleted = roadmaps.reduce((sum, roadmap) => sum + roadmap.progress.completed, 0);
    const totalSteps = roadmaps.reduce((sum, roadmap) => sum + roadmap.progress.total, 0);
    const percentage = totalSteps > 0 ? Math.round((totalCompleted / totalSteps) * 100) : 0;

    return { completed: totalCompleted, total: totalSteps, percentage };
  }

  /**
   * Calculate course-level scores
   */
  calculateCourseScores(roadmaps: DashboardRoadmap[]): { highestScore: number | null; averageScore: number | null } {
    const scores = roadmaps
      .map(roadmap => roadmap.highestScore)
      .filter((score): score is number => score !== null);

    if (scores.length === 0) {
      return { highestScore: null, averageScore: null };
    }

    const highestScore = Math.max(...scores);
    const averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;

    return {
      highestScore: Math.round(highestScore * 10) / 10,
      averageScore: Math.round(averageScore * 10) / 10
    };
  }

  /**
   * Determine course status based on roadmap progress
   */
  determineCourseStatus(roadmaps: DashboardRoadmap[], overallProgress: { percentage: number }): DashboardCourse['status'] {
    if (overallProgress.percentage >= 100) return 'completed';
    if (overallProgress.percentage > 0) return 'in_progress';
    return 'not_started';
  }

  /**
   * Get last activity timestamp from roadmaps
   */
  getLastActivity(roadmaps: DashboardRoadmap[]): string | null {
    const activities = roadmaps
      .map(roadmap => roadmap.lastActivity)
      .filter((activity): activity is string => activity !== null)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    return activities[0] || null;
  }

  /**
   * Estimate time remaining based on progress
   */
  estimateTimeRemaining(progress: { completed: number; total: number }): string | null {
    const remaining = progress.total - progress.completed;
    if (remaining <= 0) return null;

    // Rough estimate: 10 minutes per step
    const minutesRemaining = remaining * 10;
    const hours = Math.floor(minutesRemaining / 60);
    const minutes = minutesRemaining % 60;

    if (hours > 0) {
      return `${hours}.${Math.round(minutes / 6)} hrs`;
    } else {
      return `${minutes} min`;
    }
  }

  /**
   * Get recent learning activity for the user - SIMPLIFIED TO AVOID ERRORS
   */
  async getRecentActivity(userId: string, limit: number = 10): Promise<ProgressActivity[]> {

    try {
      // Simplified query to avoid foreign key issues
      const { data: responses, error } = await supabase
        .from('user_field_responses')
        .select('id, created_at, value_type, grading_response')
        .eq('user_id', userId)
        .eq('included', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error || !responses?.length) {
        return [];
      }

      return responses.map(response => {
        const gradingResponse = response.grading_response as any;

        let type: ProgressActivity['type'] = 'step_completed';
        let points: number | undefined;

        if (response.value_type === 'quiz_result' || response.value_type === 'matching_quiz_result') {
          type = 'quiz_completed';
          points = gradingResponse?.score || 0;
        }

        return {
          id: response.id,
          type,
          title: 'Step Completed',
          timestamp: response.created_at,
          points
        };
      });

    } catch (error) {
      console.error('❌ DASHBOARD: Error loading recent activity:', error);
      return [];
    }
  }

  /**
   * Get score trends over time for analytics - SIMPLIFIED TO AVOID ERRORS
   */
  async getScoreTrends(userId: string, days: number = 30): Promise<ScoreTrend[]> {

    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      // Simplified query to avoid foreign key issues
      const { data: responses, error } = await supabase
        .from('user_field_responses')
        .select('created_at, grading_response, value_type')
        .eq('user_id', userId)
        .not('grading_response', 'is', null)
        .gte('created_at', cutoffDate.toISOString())
        .order('created_at', { ascending: true });

      if (error || !responses?.length) {
        return [];
      }

      return responses
        .map(response => {
          const gradingResponse = response.grading_response as any;
          if (!gradingResponse?.score) return null;

          const type = (response.value_type === 'quiz_result' || response.value_type === 'matching_quiz_result') ? 'quiz' : 'execution';

          return {
            date: response.created_at.split('T')[0], // Get date part only
            score: gradingResponse.score,
            courseId: '',
            courseName: 'Course',
            type
          } as ScoreTrend;
        })
        .filter((trend): trend is ScoreTrend => trend !== null);

    } catch (error) {
      console.error('❌ DASHBOARD: Error loading score trends:', error);
      return [];
    }
  }

  /**
   * Get overall dashboard statistics
   */
  async getDashboardStats(userId: string): Promise<DashboardStats> {

    try {
      const courses = await this.getUserCourses(userId);

      const totalCourses = courses.length;
      const completedCourses = courses.filter(c => c.status === 'completed').length;
      const totalPoints = courses.reduce((sum, c) => sum + c.currentPoints, 0);

      // Calculate average score across all courses
      const courseScores = courses
        .map(c => c.bestCombinedScore)
        .filter((score): score is number => score !== null);
      const averageScore = courseScores.length > 0
        ? courseScores.reduce((sum, score) => sum + score, 0) / courseScores.length
        : 0;

      // Get last activity
      const lastActivityDate = courses
        .map(c => c.lastActivity)
        .filter((date): date is string => date !== null)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;

      return {
        totalCourses,
        completedCourses,
        totalPoints,
        averageScore: Math.round(averageScore * 10) / 10,
        streakDays: 0, // Could be enhanced with actual streak calculation
        lastActivityDate
      };

    } catch (error) {
      console.error('❌ DASHBOARD: Error calculating stats:', error);
      return {
        totalCourses: 0,
        completedCourses: 0,
        totalPoints: 0,
        averageScore: 0,
        streakDays: 0,
        lastActivityDate: null
      };
    }
  }
}

export const dashboardService = new DashboardService();
